import type { FC, ReactNode } from 'react'
import type { GetStaticProps } from 'next'
import type { ContentListItem, TopicRow } from '@/lib/content/types'

import { useMemo } from 'react'
import { Layout } from '@/components/generals/layout'
import { absoluteUrl, Seo } from '@/components/generals/seo'
import { CtaBand, PageHero } from '@/components/site/primitives'
import { ArticleListing, topicFilters } from '@/components/site/article-listing'
import { listInsights, listTopics } from '@/lib/content/queries'
import { localePath } from '@/i18n/config'
import { useLocale } from '@/i18n/use-locale'
import { siteContent } from '@/i18n/site-content'

interface InsightsPageProps {
    items: Array<ContentListItem>
    topics: Array<TopicRow>
}

const filterOf = (item: ContentListItem): string | null => item.topic_id

const InsightsPage: FC<InsightsPageProps> = ({ items, topics }): ReactNode => {
    const locale = useLocale()
    const copy = siteContent[locale]
    const page = copy.insights
    const filters = useMemo(() => topicFilters(items, topics, locale), [items, topics, locale])

    return (
        <Layout tone='dark'>
            <Seo
                title={page.seoTitle}
                description={page.seoDescription}
                path='/insights'
                breadcrumbs={[{ name: page.seoTitle, path: '/insights' }]}
                jsonLd={[{
                    '@context': 'https://schema.org',
                    '@type': 'CollectionPage',
                    name: page.seoTitle,
                    description: page.seoDescription,
                    url: absoluteUrl(localePath(locale, '/insights'))
                }]}
            />

            <PageHero tone='dark' eyebrow={page.eyebrow} title={page.title} text={page.text} />

            <ArticleListing
                items={items}
                filters={filters}
                filterOf={filterOf}
                allLabel={copy.listing.allTopics}
                filterLabel={copy.listing.filterByTopic}
                emptyTitle={page.emptyTitle}
                emptyText={page.emptyText}
            />

            <CtaBand />
        </Layout>
    )
}

export const getStaticProps: GetStaticProps<InsightsPageProps> = async () => {
    try {
        const [items, topics] = await Promise.all([listInsights(), listTopics()])
        return { props: { items, topics }, revalidate: 60 }
    }
    catch (error) {
        console.error('Could not load insights', error)
        return { props: { items: [], topics: [] }, revalidate: 60 }
    }
}

export default InsightsPage
